import {
  Command,
  CommandGroup,
  CommandItem,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { Spinner } from "@/components/ui/spinner";
import { CornerDownRight, CornerDownLeft } from "lucide-react";
import {
  Tools,
  ITool,
  ICollection,
  IAutocompleteItem,
  IMcpTool,
} from "@/types";
import { cn } from "@/utils/cn";
import ToolIcon from "./agent-tool-icons";
import { useTranslation } from "react-i18next";

interface IMenuProps {
  isOpen: boolean;
  tools: Tools;
  onOpenChange: (open: boolean) => void;
  position: { top?: number; left: number; bottom?: number };
  onSelect: (tool: ITool) => void;
  onCollectionSelect: (
    collection: ICollection | IAutocompleteItem | IMcpTool,
  ) => void;
  menuType: "tools" | "collections";
  collections?: Array<ICollection | IAutocompleteItem | IMcpTool>;
  isLoading: boolean;
  selectedIndex: number;
  selectedTool?: ITool;
  inline?: boolean;
}

export const MenuSelection = ({
  isOpen,
  tools,
  position,
  onSelect,
  menuType = "tools",
  collections,
  onCollectionSelect,
  isLoading,
  selectedIndex,
  searchQuery,
  onSearchChange,
  selectedTool,
  inline = false,
}: IMenuProps & {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const selectedValue =
    menuType === "tools"
      ? tools[selectedIndex]?.id
      : collections?.[selectedIndex]?.id;
  return isOpen ? (
    <div
      style={{
        ...(inline
          ? {}
          : {
              top: position.top,
              insetInlineStart: position.left,
              bottom: position.bottom,
            }),
        scrollbarColor: "var(--primary) transparent",
      }}
      contentEditable={false}
      className={cn(
        "w-[209px] max-w-[95vw] z-[1500] overflow-y-auto py-2 px-1 rounded-2xl border-accent border-solid border-[1px] bg-[var(--card-background)] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:bg-transparent",
        inline ? "relative mt-2" : "fixed",
        {
          "flex items-center justify-center h-32": isLoading,
        },
      )}
    >
      <Command value={selectedValue} shouldFilter={false}>
        <CommandInput
          value={searchQuery}
          onValueChange={onSearchChange}
          placeholder={t("playground.searchPlaceholder")}
          className="h-8 border-b-[var(--border)] text-sm focus:outline-hidden"
        />
        <CommandList>
          {!isLoading ? (
            <CommandGroup
              heading={selectedTool?.name ?? t("playground.toolLabel")}
            >
              {menuType === "tools"
                ? tools.map((tool) => (
                    <CommandItem
                      value={tool.id}
                      onSelect={() => {
                        onSelect(tool);
                      }}
                      key={tool.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <ToolIcon
                          name={
                            tool?.icon ||
                            (tool.type === "mcp" ? "blocks" : "wrench")
                          }
                          className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2"
                        />
                        {tool.name}
                      </div>
                      {(tool.has_autocomplete || tool.type === "mcp") &&
                        (isRTL ? <CornerDownLeft /> : <CornerDownRight />)}
                    </CommandItem>
                  ))
                : collections?.map((collection) => {
                    const subtext =
                      "source" in collection && collection.source
                        ? collection.source
                        : undefined;

                    return (
                      <CommandItem
                        value={collection.id}
                        onSelect={() => {
                          onCollectionSelect(collection);
                        }}
                        key={collection.id}
                        className="flex items-center gap-2"
                      >
                        <ToolIcon
                          name={
                            selectedTool?.icon ||
                            ((collection as any).type === "collection"
                              ? "library-big"
                              : (collection as any).type === "document"
                                ? "file-text"
                                : (collection as any).type === "flow"
                                  ? "workflow"
                                  : selectedTool?.type === "mcp"
                                    ? (collection as any).icon || "blocks"
                                    : "wrench")
                          }
                          className="w-4 h-4"
                        />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">
                            {collection.name}
                          </div>
                          {subtext ? (
                            <div className="truncate text-xs text-muted-foreground">
                              {subtext}
                            </div>
                          ) : null}
                        </div>
                      </CommandItem>
                    );
                  })}
            </CommandGroup>
          ) : (
            <div className="flex items-center justify-center py-4">
              <Spinner />
            </div>
          )}
        </CommandList>
      </Command>
    </div>
  ) : null;
};
